import {
  exampleWorkspaceHelpThreadRouteId,
  isExampleHelpAccessActive,
} from '@/convex/auth/example_threads/route_ids';

export const FREE_HELP_SEED_BOOTSTRAP_TIMEOUT_MS = 15_000;
export const FREE_HELP_SEED_BOOTSTRAP_MAX_WORKSPACE_AGE_MS = 2 * 60 * 1000;
const FREE_HELP_SEEN_STORAGE_KEY_PREFIX = 'imai.free-help-seen';

type FreeWorkspacePlan = string | null | undefined;

export function isFreeWorkspacePlan(plan: FreeWorkspacePlan): boolean {
  return plan === 'free';
}

export function getWorkspaceSidebarFooterMode(
  plan: FreeWorkspacePlan
): 'free-upgrade' | 'support-feedback' {
  return isFreeWorkspacePlan(plan) ? 'free-upgrade' : 'support-feedback';
}

export function shouldShowFreeWorkspacePricing(
  plan: FreeWorkspacePlan
): boolean {
  return isFreeWorkspacePlan(plan);
}

export function freeWorkspaceHelpSeenStorageKey(
  organizationId: string
): string {
  return `${FREE_HELP_SEEN_STORAGE_KEY_PREFIX}:${organizationId}`;
}

export function hasSeenFreeWorkspaceHelp(organizationId: string): boolean {
  try {
    return (
      window.localStorage.getItem(
        freeWorkspaceHelpSeenStorageKey(organizationId)
      ) === '1'
    );
  } catch {
    return false;
  }
}

export function markFreeWorkspaceHelpSeen(organizationId: string): void {
  try {
    window.localStorage.setItem(
      freeWorkspaceHelpSeenStorageKey(organizationId),
      '1'
    );
  } catch {
    // Storage can be unavailable in private browsing or hardened contexts.
  }
}

export function shouldRedirectToFreeWorkspaceHelp(args: {
  billingPlan: FreeWorkspacePlan;
  creditBalance: number | null | undefined;
  organizationId: string | null | undefined;
  workspaceCreatedAt: number | null | undefined;
  helpSeen: boolean;
  now?: number;
}): boolean {
  return (
    isFreeWorkspacePlan(args.billingPlan) &&
    typeof args.creditBalance === 'number' &&
    args.creditBalance <= 0 &&
    Boolean(args.organizationId) &&
    typeof args.workspaceCreatedAt === 'number' &&
    isExampleHelpAccessActive(args.workspaceCreatedAt, args.now) &&
    !args.helpSeen
  );
}

export function isSeededHelpThreadRoute(args: {
  routeId: string | null | undefined;
  organizationId: string | null | undefined;
}): boolean {
  if (!args.routeId || !args.organizationId) return false;
  return (
    args.routeId === exampleWorkspaceHelpThreadRouteId(args.organizationId)
  );
}

export function canUseTemporaryFreeHelpChat(args: {
  billingPlan: FreeWorkspacePlan;
  organizationId: string | null | undefined;
  workspaceCreatedAt: number | null | undefined;
  threadRouteId: string | null | undefined;
  now?: number;
}): boolean {
  if (!isFreeWorkspacePlan(args.billingPlan)) return false;
  if (
    !isSeededHelpThreadRoute({
      routeId: args.threadRouteId,
      organizationId: args.organizationId,
    })
  ) {
    return false;
  }
  if (typeof args.workspaceCreatedAt !== 'number') return false;
  return isExampleHelpAccessActive(args.workspaceCreatedAt, args.now);
}

export function canRedirectToTemporaryFreeHelp(args: {
  billingPlan: FreeWorkspacePlan;
  creditBalance: number | null | undefined;
  organizationId: string | null | undefined;
  workspaceCreatedAt: number | null | undefined;
  now?: number;
}): boolean {
  if (!isFreeWorkspacePlan(args.billingPlan)) return false;
  if (!args.organizationId) return false;
  if (typeof args.creditBalance !== 'number' || args.creditBalance > 0) {
    return false;
  }
  if (typeof args.workspaceCreatedAt !== 'number') return false;
  return isExampleHelpAccessActive(args.workspaceCreatedAt, args.now);
}

export function shouldWaitForSeededHelpThread(args: {
  billingPlan: FreeWorkspacePlan;
  organizationId: string | null | undefined;
  workspaceCreatedAt: number | null | undefined;
  threadRouteId: string | null | undefined;
  threadFound: boolean;
  bootstrapExpired: boolean;
  now?: number;
}): boolean {
  if (args.threadFound || args.bootstrapExpired) return false;
  if (
    !canUseTemporaryFreeHelpChat({
      billingPlan: args.billingPlan,
      organizationId: args.organizationId,
      workspaceCreatedAt: args.workspaceCreatedAt,
      threadRouteId: args.threadRouteId,
      now: args.now,
    })
  ) {
    return false;
  }
  if (typeof args.workspaceCreatedAt !== 'number') return false;
  const age = (args.now ?? Date.now()) - args.workspaceCreatedAt;
  return age <= FREE_HELP_SEED_BOOTSTRAP_MAX_WORKSPACE_AGE_MS;
}
